import React, { useState, useMemo, useEffect, useRef, useContext } from 'react';
import classes from './NeedScreen.module.css';
import { db } from '../../config';
import { doc, updateDoc } from 'firebase/firestore';
import SearchAndCategories from '../Checklist/SearchAndCategories';
import ItemDetail from './ItemDetail';
import InlineAdd from './InlineAdd';
import BorrowScreen from '../Share/BorrowScreen';
import useDragItem from '../../hooks/useDragItem';
import { getItemCategories, restoreScrollPosition, matchCatalogItem } from '../../config/helpers';
import { UserContext } from '../../store/user-context';
import HirablePopover from './HirablePopover';

// Retired along with the dismissible "Ask your mates" banner card — kept
// only as a fallback "already seen" check (see showAskMatesFooterHint)
// so a user who already dismissed/engaged with the old banner doesn't get
// re-nudged by its replacement coach mark below.
const ASK_MATES_TOOLTIP_KEY = 'tooltip_ask_mates_seen';
const ASK_MATES_FOOTER_TOOLTIP_KEY = 'tooltip_ask_mates_footer_seen';
const MOVE_TO_PACK_TOOLTIP_KEY = 'tooltip_move_to_pack_seen';
// Retired along with the swipe-to-reveal gesture (and its one-time
// animated hint) — this one-off cleanup drops the flags from any browser
// that already set them, so they don't linger as dead storage.
const RETIRED_SWIPE_KEYS = ['tooltip_need_swipe_seen'];
const RETIRED_SWIPE_SESSION_KEYS = ['need_swipe_gesture_used'];

// Row exit animation (slide right, then collapse) — kept as one JS constant
// since the delayed Firestore write (see commitMoveToPack) has to wait the
// same total duration the CSS transitions below actually take to play.
const MOVE_TO_PACK_SLIDE_MS = 220;
const MOVE_TO_PACK_COLLAPSE_MS = 200;
const MOVE_TO_PACK_ANIMATION_MS = MOVE_TO_PACK_SLIDE_MS + MOVE_TO_PACK_COLLAPSE_MS;
const MOVE_TO_PACK_TOAST_MS = 4000;

// Computed once, not per-render — same reasoning as useDragItem.js's
// isTouchCapable (a live OS-setting change mid-session is an edge case not
// worth a MediaQueryList listener for here).
const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Same "i in a circle" glyph as Checklist.js's OwnNeedTooltip — every coach
// mark in the app uses this same icon + X-close pattern now (not a text
// "Got it" link), so they read as one consistent system rather than one-off
// callouts each shaped around whatever they happen to point at.
const InfoIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="var(--brand-primary, #2be494)" strokeWidth="1.6"/>
    <circle cx="12" cy="8" r="1" fill="var(--brand-primary, #2be494)"/>
    <path d="M12 11v5" stroke="var(--brand-primary, #2be494)" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);

const TooltipCloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M18 6L6 18M6 6L18 18" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// One-time coach mark pointing up at the "Ask your mates" pill inside the
// summary bar (see summaryBar in NeedScreen's render). Rendered as a
// sibling AFTER that bar — not a `position: absolute` child of it — so it
// occupies real space in normal flow and pushes the list down instead of
// floating on top of (and hiding) the first row underneath it. The arrow
// still points at the pill: .askMatesFooterTooltip's own right-margin plus
// the arrow's own right-offset add up to the same horizontal position the
// pill sits at in the bar above.
const AskMatesFooterTooltip = ({ onDismiss }) => {
  const [dismissing, setDismissing] = useState(false);

  const handleDismiss = () => {
    localStorage.setItem(ASK_MATES_FOOTER_TOOLTIP_KEY, 'true');
    setDismissing(true);
    // Let the fade-out play before actually removing it from the tree
    setTimeout(onDismiss, 200);
  };

  return (
    <div
      className={`${classes.askMatesFooterTooltip} ${dismissing ? classes.askMatesFooterTooltipHidden : ''}`}
      onClick={handleDismiss}
    >
      <span className={classes.askMatesFooterTooltipArrow} aria-hidden="true" />
      <span className={classes.askMatesFooterTooltipIcon} aria-hidden="true"><InfoIcon /></span>
      <div className={classes.askMatesFooterTooltipBody}>
        <p className={classes.askMatesFooterTooltipHeading}>Missing something?</p>
        <p className={classes.askMatesFooterTooltipText}>It's probably in someone's garage.<br />Ask&nbsp;and&nbsp;see.</p>
      </div>
      <button
        className={classes.askMatesFooterTooltipClose}
        onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
        aria-label="Dismiss"
      >
        <TooltipCloseIcon />
      </button>
    </div>
  );
};

// One-time coach mark for the new "Move to Pack" button — mirrors
// Checklist.js's OwnNeedTooltip (bordered callout, arrow pointing up at the
// row above) rather than a floating measured popover, since this is a
// static, always-same-position hint under the first row, not something
// anchored to a specific element's live coordinates.
const MoveToPackTooltip = ({ onDismiss }) => {
  const [dismissing, setDismissing] = useState(false);

  const handleDismiss = () => {
    localStorage.setItem(MOVE_TO_PACK_TOOLTIP_KEY, 'true');
    setDismissing(true);
    // Let the fade-out play before actually removing it from the tree
    setTimeout(onDismiss, 200);
  };

  // Only the X dismisses this — unlike AskMatesFooterTooltip, which is a
  // tap-anywhere-on-card dismiss target.
  return (
    <div
      className={`${classes.moveToPackTooltip} ${dismissing ? classes.moveToPackTooltipHidden : ''}`}
    >
      <span className={classes.moveToPackTooltipArrow} aria-hidden="true" />
      <span className={classes.moveToPackTooltipIcon} aria-hidden="true"><InfoIcon /></span>
      <div className={classes.moveToPackTooltipBody}>
        <p className={classes.moveToPackTooltipHeading}>Sorted it?</p>
        <p className={classes.moveToPackTooltipText}>Tap the button to move it to Pack.</p>
      </div>
      <button
        className={classes.moveToPackTooltipClose}
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        <TooltipCloseIcon />
      </button>
    </div>
  );
};

// "<Item> moved to Pack" toast with Undo — replaces the old, unrelated
// hardcoded-blue "Added to your pack list" toast that used to live here.
// Always sits at the same base bottom-nav clearance (see .moveToPackToast's
// own comment for the exact numbers) — it no longer needs to shift up for a
// separate floating footer button, since that button was merged into the
// (non-floating) sticky summary bar at the top of the screen instead.
const MoveToPackToast = ({ itemName, onUndo }) => (
  <div className={classes.moveToPackToast} role="status" aria-live="polite">
    <span className={classes.moveToPackToastText}>{itemName} moved to Pack</span>
    <button className={classes.moveToPackToastUndo} onClick={onUndo}>
      Undo
    </button>
  </div>
);

const DragHandleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M4 7H20M4 12H20M4 17H20" stroke="#c8cccc" strokeWidth="2.2" strokeLinecap="round"/>
  </svg>
);

// Same artwork as the bottom nav's Need tab icon (see BottomTabs.js's
// NeedIcon) — filled with the festival's brand colour instead of the
// nav's active/inactive black-or-grey.
const HandshakeIcon = ({ color = '#2be494' }) => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
    <path d="M17.7402 3.53125C17.1107 3.33094 16.4522 3.24245 15.7939 3.26562L9.23728 9.82324C9.19046 9.87008 9.16408 9.93377 9.16404 10C9.16404 10.0662 9.19053 10.1299 9.23728 10.1768L9.78025 10.7197C10.1774 11.1167 10.8226 11.1168 11.2197 10.7197L12.2197 9.71972L12.3603 9.58496C13.0816 8.93183 14.0223 8.5684 15 8.56836L15.1953 8.57324C16.1669 8.62156 17.0891 9.02866 17.7803 9.71972L20.5605 12.5C20.8748 12.0918 21.1353 11.6427 21.3301 11.1592C21.6256 10.4253 21.7666 9.6375 21.7441 8.84668C21.7215 8.05604 21.536 7.27819 21.1992 6.5625C20.9044 5.93606 20.4993 5.36809 20.0039 4.88672L19.7861 4.68554C19.191 4.16418 18.4941 3.77121 17.7402 3.53125Z" fill={color}/>
    <path d="M8.59763 3.2705C7.90696 3.19683 7.20867 3.24922 6.53806 3.42383L6.25291 3.50586C5.49632 3.74522 4.7973 4.13947 4.20017 4.66211C3.67777 5.11945 3.24251 5.66743 2.91599 6.27832L2.7822 6.54394C2.44487 7.26232 2.2602 8.04356 2.23923 8.83691C2.21834 9.6303 2.36209 10.4201 2.66111 11.1553C2.95929 11.8882 3.40633 12.552 3.97263 13.1045L11.4726 20.5322C11.7649 20.8217 12.236 20.8217 12.5283 20.5322L14.5088 18.5693L11.9697 16.0303L11.9179 15.9736C11.6777 15.6791 11.6951 15.2443 11.9697 14.9697C12.2443 14.6952 12.6791 14.6778 12.9736 14.918L13.0303 14.9697L15.5742 17.5137L17.0195 16.0801L14.4697 13.5303L14.4179 13.4736C14.1777 13.1791 14.1951 12.7443 14.4697 12.4697C14.7443 12.1952 15.1791 12.1778 15.4736 12.418L15.5303 12.4697L18.0859 15.0254L19.5322 13.5928L16.7197 10.7803C16.3207 10.3813 15.7969 10.1354 15.2402 10.0801L15 10.0684C14.4357 10.0684 13.8917 10.2646 13.459 10.6191L13.2803 10.7803L12.2803 11.7803C11.2974 12.7631 9.7026 12.7631 8.7197 11.7803L8.17673 11.2373C7.84877 10.9091 7.66404 10.4639 7.66404 10C7.66408 9.53603 7.84871 9.09083 8.17673 8.76269L12.0029 4.87402C11.7252 4.60303 11.4211 4.35958 11.0937 4.14941L10.8388 3.99511C10.1498 3.60162 9.38661 3.35475 8.59763 3.2705Z" fill={color}/>
  </svg>
);

// "All sorted" empty state — a plain check-in-circle, distinct from
// HandshakeIcon (used for "nothing needed yet") so the two empty states
// read differently at a glance.
const AllSortedIcon = ({ color = '#2be494' }) => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9.25" stroke={color} strokeWidth="1.5"/>
    <path d="M7.5 12.5L10.5 15.5L16.5 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M9 6L15 12L9 18" stroke="#565959" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const getBadge = (item, isHirable) => {
  if (item.borrowStatus === 'offered') {
    const offerer = item.offeredBy;
    return {
      label: `Offered${offerer ? ` by ${offerer}` : ''}`,
      className: classes.badgeOffered,
    };
  }
  if (item.borrowStatus === 'requested') return {
    label: 'Requested',
    className: classes.badgeRequested,
  };
  if (item.borrowStatus === 'hired' || item.borrowStatus === 'borrow-pending') return {
    label: 'Hired',
    className: classes.badgeHired,
  };
  // Lowest priority — an explicit borrowStatus above always wins. Mirrors
  // GearCard.js's STATUS_META fallback order.
  if (isHirable) return {
    label: 'Hirable',
    className: classes.badgeHirable,
    // Only this badge opens the hire popover — the type flag lets the
    // renderer tell it apart from Offered/Requested/Hired without
    // comparing against a CSS module class reference.
    type: 'hirable',
  };
  return null;
};

// Tabler's package-export — a package with an arrow, reads as "this is
// heading out" (i.e. to Pack), distinct from the plain tick it replaces.
const MoveToPackIcon = () => <i className="ti ti-package-export" style={{ fontSize: 16 }} aria-hidden="true" />;

const NeedItem = ({ item, onMoveToPack, onDetail, isHirable, isSorting, handleDragStart, handleDragOver, handleDragEnd, festivalName, hireUrl }) => {
  const needQty = item?.needQuantity || 0;
  const badge = getBadge(item, isHirable);
  const category = getItemCategories(item)[0];

  const { cardRef, isDragging, isDragOver, dragHandlers, dataAttrs } = useDragItem({
    id: item?.id,
    name: item?.name,
    category,
    onDragStart: handleDragStart,
    onDragOver: handleDragOver,
    onDragEnd: handleDragEnd,
  });

  const [showHirePopover, setShowHirePopover] = useState(false);
  const hireBadgeRef = useRef(null);

  // Two-phase exit animation, driven entirely by the isSorting prop (owned
  // by NeedScreen, not local state — Undo needs to be able to cancel it
  // from outside). Phase 1 (slide) applies immediately; phase 2 (collapse)
  // is staged in on its own timer so the two CSS transitions run in
  // sequence rather than simultaneously. See NeedScreen's
  // MOVE_TO_PACK_SLIDE_MS/COLLAPSE_MS — this local timer only decides when
  // to add the second class, the actual Firestore write is scheduled
  // separately (and identically timed) by the parent.
  const [collapsing, setCollapsing] = useState(false);
  useEffect(() => {
    if (!isSorting) {
      setCollapsing(false);
      return undefined;
    }
    if (prefersReducedMotion) return undefined;
    const t = setTimeout(() => setCollapsing(true), MOVE_TO_PACK_SLIDE_MS);
    return () => clearTimeout(t);
  }, [isSorting]);

  const handleMoveToPackClick = () => {
    if (isSorting) return;
    onMoveToPack && onMoveToPack(item);
  };

  const rowClasses = [
    classes.card,
    isDragging ? classes.cardDragging : '',
    isDragOver ? classes.cardDragOver : '',
    isSorting && !prefersReducedMotion ? classes.cardSorting : '',
    collapsing && !prefersReducedMotion ? classes.cardCollapsing : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={cardRef}
      {...dataAttrs}
      className={rowClasses}
      {...dragHandlers}
    >

      {/* Single row: handle, name + badge, move-to-pack button, chevron */}
      <div className={classes.nameRow}>
        {handleDragStart && (
          <span
            className={classes.dragHandle}
            aria-hidden="true"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <DragHandleIcon />
          </span>
        )}
        <div className={classes.nameAndBadge} draggable={false}>
          <span className={classes.itemName}>
            {item.name}
            {needQty > 1 && <span className={classes.itemQty}> ×{needQty}</span>}
          </span>
          {badge && (
            badge.type === 'hirable' ? (
              <button
                ref={hireBadgeRef}
                className={`${classes.badge} ${badge.className} ${classes.badgeTappable}`}
                draggable={false}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => setShowHirePopover(v => !v)}
              >
                {badge.label}
              </button>
            ) : (
              <span className={`${classes.badge} ${badge.className}`}>{badge.label}</span>
            )
          )}
          {showHirePopover && (
            <HirablePopover
              anchorRef={hireBadgeRef}
              festivalName={festivalName}
              hireUrl={hireUrl}
              onClose={() => setShowHirePopover(false)}
            />
          )}
        </div>
        <button
          className={classes.moveToPackBtn}
          draggable={false}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={handleMoveToPackClick}
          disabled={isSorting}
          aria-label={`Move ${item.name} to Pack`}
        >
          <span className={classes.moveToPackBtnCircle}>
            <MoveToPackIcon />
          </span>
        </button>
        <button
          className={classes.chevronBtn}
          draggable={false}
          onClick={() => onDetail && onDetail(item)}
        >
          <ChevronIcon />
        </button>
      </div>

    </div>
  );
};

const NeedScreen = ({ group, festivalInfo, activeCategory, setActiveCategory, setCurrentScreen, onAddItem, onShowToast, onAddCategory, onRenameCategory, onDeleteCategory, onSetCategoryIcon, onReorderCategories, onSortedToPack }) => {
  const UserCtx = useContext(UserContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  // ItemDetail intentionally scrolls `.content` to the top on open (see
  // ItemDetail.js) — since it's the same shared scroll container as this
  // list, that leaves the list itself sitting at scrollTop 0 underneath.
  // Captured here right before opening detail, then reapplied on the way
  // back so returning lands on the same item/category instead of the top.
  const savedScrollRef = useRef(0);
  const openItemDetail = (item) => {
    const contentEl = document.querySelector('.content');
    if (contentEl) savedScrollRef.current = contentEl.scrollTop;
    setSelectedItem(item);
  };
  const closeItemDetail = () => {
    setSelectedItem(null);
    restoreScrollPosition(savedScrollRef.current);
  };
  const [inlineAdding, setInlineAdding] = useState(false);
  const [showBorrowScreen, setShowBorrowScreen] = useState(false);
  const [askMatesFooterHintDismissed, setAskMatesFooterHintDismissed] = useState(false);
  const preCategoryRef = useRef(activeCategory);

  // "Move to Pack" — rows currently mid-exit-animation (isSorting on
  // NeedItem), the "moved to Pack" toast + its Undo snapshot, and the
  // per-item pending-write timers Undo needs to be able to cancel before
  // they land. See handleMoveToPack/commitMoveToPack/handleUndo below.
  const [sortingIds, setSortingIds] = useState(() => new Set());
  const [moveToPackToast, setMoveToPackToast] = useState(null); // { itemId, itemName, snapshot } | null
  const pendingSortsRef = useRef({}); // itemId -> { timer, snapshot }
  const toastTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      Object.values(pendingSortsRef.current).forEach(p => clearTimeout(p.timer));
      clearTimeout(toastTimerRef.current);
    };
  }, []);

  // Keep preCategoryRef updated with the last explicitly-selected non-All category
  useEffect(() => {
    if (activeCategory !== '') preCategoryRef.current = activeCategory;
  }, [activeCategory]);

  // Typing a search query while a specific category tab is active would
  // otherwise search only within that category — invisible to the user,
  // and reads as "search is broken" for any match outside it. Switch to
  // All while searching, then restore the category once the query clears.
  const handleSearchChange = (query) => {
    if (query && activeCategory !== '') {
      preCategoryRef.current = activeCategory;
      setActiveCategory('');
    } else if (!query) {
      setActiveCategory(preCategoryRef.current || '');
    }
    setSearchQuery(query);
  };

  // Root.js swaps Home/My Gear/Need/Pack in and out of the same shared
  // `.content` scroll container rather than mounting a fresh scrollable
  // wrapper per screen, so without this Need opens at whatever scroll
  // position the previous screen was left at. Reapplied on the next frame
  // too — on iOS Safari a tap can leave momentum/rubber-band scrolling still
  // settling on `.content`, which can silently override a scrollTop reset
  // that only happens once, synchronously, on mount.
  useEffect(() => {
    const contentEl = document.querySelector('.content');
    if (contentEl) contentEl.scrollTop = 0;
    const raf = requestAnimationFrame(() => {
      if (contentEl) contentEl.scrollTop = 0;
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  // Icon `stroke` props are drawn as SVG, which can't read CSS variables —
  // derive the value directly from the festival doc, same as Root.js does
  // when it sets the CSS custom property.
  const brandPrimary = festivalInfo?.brandColorPrimary || '#2be494';

  // Drag-and-drop reordering — independent of My Gear's `order` field
  const [currentDrag, setCurrentDrag] = useState();
  const [currentDragOver, setCurrentDragOver] = useState();
  const [dragWorking, setDragWorking] = useState(false);

  // Get all items where needQuantity > 0, sorted by needOrder (falling back
  // to My Gear's `order` field for items never manually reordered here).
  const needItems = useMemo(() => {
    if (!group?.items) return [];
    const filtered = group.items.filter(item => {
      const needQty = item?.needQuantity !== undefined
        ? item.needQuantity
        : (item?.organise || []).filter(o => o.method === 'Borrow' || o.method === 'Hire').length;
      return needQty > 0;
    });
    return [...filtered].sort((a, b) => {
      const aOrder = a.needOrder !== undefined ? a.needOrder : (a.order ?? 0);
      const bOrder = b.needOrder !== undefined ? b.needOrder : (b.order ?? 0);
      return aOrder - bOrder;
    });
  }, [group]);

  // One-time "Ask your mates" coach mark — same 3+ Need items threshold the
  // old banner used. Also checks the old banner's own key: a user who
  // already dismissed/engaged with that banner has already been told about
  // this feature once and shouldn't be re-nudged just because the UI
  // changed underneath them. Move-to-pack (below) now has priority — this
  // only shows once MOVE_TO_PACK_TOOLTIP_KEY is set, i.e. after that hint
  // has been seen/dismissed, so the two never show at once and the user
  // always meets "move it to Pack" before "ask your mates" for it. Read
  // live from localStorage on every render (not cached in state), so
  // dismissing move-to-pack — which sets this same key — makes this one
  // eligible to appear on the very next render, no reload needed.
  const showAskMatesFooterHint = !askMatesFooterHintDismissed
    && needItems.length >= 3
    && localStorage.getItem(ASK_MATES_FOOTER_TOOLTIP_KEY) !== 'true'
    && localStorage.getItem(ASK_MATES_TOOLTIP_KEY) !== 'true'
    && localStorage.getItem(MOVE_TO_PACK_TOOLTIP_KEY) === 'true';

  const dismissAskMatesFooterHint = () => setAskMatesFooterHintDismissed(true);

  // Distinguishes "just sorted the last item" (show the All-sorted state)
  // from "opened Need with nothing in it to begin with" (show the plain
  // empty state) — both are needItems.length === 0, which can't tell them
  // apart from the data alone. Same transition-detection shape as
  // PackScreen.js's own wasAllPackedRef (used there to fire confetti once).
  const hadNeedItemsRef = useRef(null);
  const [justFinishedSorting, setJustFinishedSorting] = useState(false);
  useEffect(() => {
    const hasItems = needItems.length > 0;
    if (hadNeedItemsRef.current === null) {
      hadNeedItemsRef.current = hasItems;
      return;
    }
    if (!hasItems && hadNeedItemsRef.current) setJustFinishedSorting(true);
    hadNeedItemsRef.current = hasItems;
  }, [needItems.length]);

  // One-time "Move to Pack" coach mark — now takes priority over the
  // Ask-your-mates footer coach mark (see showAskMatesFooterHint above,
  // which defers to this one via MOVE_TO_PACK_TOOLTIP_KEY): shows as soon
  // as its own condition is met, regardless of the other hint's state.
  // Checklist.js's own Own/Need coach mark lives on a different screen
  // entirely (My Gear), so it can never render alongside either of these
  // two regardless — no extra check needed for it.
  const [moveToPackHintDismissed, setMoveToPackHintDismissed] = useState(false);
  const showMoveToPackHint = !moveToPackHintDismissed
    && needItems.length > 0
    && localStorage.getItem(MOVE_TO_PACK_TOOLTIP_KEY) !== 'true';
  const dismissMoveToPackHint = () => setMoveToPackHintDismissed(true);

  // Filter by active category
  const filteredItems = useMemo(() => {
    let items = needItems;
    if (activeCategory) {
      items = items.filter(item => getItemCategories(item).includes(activeCategory));
    }
    if (searchQuery) {
      items = items.filter(item =>
        item.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return items;
  }, [needItems, activeCategory, searchQuery]);

  // Group filtered items by category (used in All view only)
  const groupedItems = useMemo(() => {
    if (activeCategory) return null;
    return (group?.categories || [])
      .map(cat => ({ title: cat, items: filteredItems.filter(item => getItemCategories(item).includes(cat)) }))
      .filter(g => g.items.length > 0);
  }, [filteredItems, activeCategory, group?.categories]);

  // The coach mark anchors under whichever item renders first — All view
  // groups by category (first non-empty group's first item), a filtered
  // view is just its own first item.
  const firstNeedItemId = activeCategory === ''
    ? groupedItems?.[0]?.items?.[0]?.id
    : filteredItems?.[0]?.id;

  // Drag reordering — mirrors Checklist.js's handleDragStart/Over/End, but
  // scoped to needItems only: reassigns `needOrder` (not `order`) and never
  // changes an item's position in the full group.items array.
  const handleDragStart = (e, id, name, category) => {
    setCurrentDrag({ id, name, category });
  };

  const handleDragOver = (e, id, name, category) => {
    e?.preventDefault();
    // Bail out when the hovered card hasn't actually changed — dragover
    // fires continuously (native drag every frame, touch on every
    // touchmove), and setting a brand-new object every time would
    // re-render the whole list for the entire drag gesture instead of
    // only when there's actually something new to reflect.
    setCurrentDragOver(prev => (prev?.id === id ? prev : { id, name, category }));
  };

  const handleDragEnd = async (e) => {
    setDragWorking(false);

    if (dragWorking || !currentDrag || !currentDragOver) return;
    if (currentDrag.id === currentDragOver.id) return;
    e?.preventDefault();
    setDragWorking(true);

    // Work on a copy of just the Need list — reordering here must not
    // touch array position for non-Need items in group.items.
    const _needItems = [...needItems];
    const dragIndex = _needItems.findIndex(item => item.id === currentDrag.id);
    const dragOverIndex = _needItems.findIndex(item => item.id === currentDragOver.id);
    if (dragIndex === -1 || dragOverIndex === -1) {
      setCurrentDrag();
      setCurrentDragOver();
      setDragWorking(false);
      return;
    }
    const dragItemContent = _needItems[dragIndex];

    // Dragging into a different category section re-tags the item, same as My Gear
    const dragItemCategories = getItemCategories(dragItemContent);
    if (!dragItemCategories.includes(currentDragOver.category)) {
      const currentCatIndex = dragItemCategories.indexOf(currentDrag.category);
      if (currentCatIndex !== -1) dragItemCategories[currentCatIndex] = currentDragOver.category;
      dragItemContent.categories = dragItemCategories;
    }

    _needItems.splice(dragIndex, 1);
    _needItems.splice(dragOverIndex, 0, dragItemContent);
    _needItems.forEach((item, index) => { item.needOrder = index; });

    // The items in _needItems are the same object references as in
    // group.items, already mutated above, so a plain shallow copy persists
    // the needOrder/category changes without disturbing array position.
    if (group?.id) {
      try {
        const groupRef = doc(db, 'groups', group.id);
        await updateDoc(groupRef, { items: [...group.items] });
      } catch (err) {
        alert('There was a problem updating your group.');
      }
    }
    setCurrentDrag();
    setCurrentDragOver();
    setDragWorking(false);
  };

  // Same live-catalog resolution GearCard.js uses — item.hire as stored on
  // the item can be stale (added before a catalog hire flag was corrected),
  // and group.festival is just the lightweight {id, name} snapshot with no
  // hireurl, so both need to come from festivalInfo (the live festival doc)
  // instead.
  // An empty array is still truthy, so a festival with no customised items
  // (useDefaultItems: true and nothing in its own items array) must fall
  // through to the shared catalog explicitly — see the same fix in
  // GearCard.js.
  const itemInfoList = (festivalInfo?.items?.length ? festivalInfo.items : UserCtx?.itemInfo) || [];
  const isItemHirable = (item) => {
    const catalogMatch = matchCatalogItem(item?.name, itemInfoList);
    return catalogMatch?.hire || false;
  };

  // For the Hirable badge's popover (see HirablePopover) — same
  // festivalInfo-over-stale-group.festival preference used everywhere else
  // in this file.
  const hireFestivalName = festivalInfo?.name || group?.festival?.name;
  const hireFestivalUrl = festivalInfo?.hireurl;

  // One-off cleanup for the retired swipe-to-reveal gesture and its hint —
  // drops the flags from any browser that already set them.
  useEffect(() => {
    RETIRED_SWIPE_KEYS.forEach(key => localStorage.removeItem(key));
    RETIRED_SWIPE_SESSION_KEYS.forEach(key => sessionStorage.removeItem(key));
  }, []);

  // Same predicate PackScreen.js uses to decide what it shows, so "top of
  // the unpacked group" is computed against the exact same set of items
  // Pack itself will display.
  const isPackVisible = (i) => {
    const ownQty = i?.ownQuantity !== undefined
      ? i.ownQuantity
      : (i?.organise || []).filter(o => o.method === 'Bring' || !o.method).length;
    return ownQty > 0;
  };

  const clearSorting = (itemId) => {
    setSortingIds(prev => {
      if (!prev.has(itemId)) return prev;
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  };

  // The actual data write — identical to NeedScreen's old handleGotIt (own
  // = own + need, need = 0, borrowStatus/offeredBy stripped; lenderName is
  // deliberately left untouched, matching that existing behaviour) plus one
  // addition: an explicit packOrder so the item lands at the top of Pack's
  // unpacked group rather than wherever its stale `order` happens to sort.
  // Reads the CURRENT group.items at call time (not a stale closure), so a
  // write that lands after other realtime edits still only touches this
  // one item.
  const commitMoveToPack = async (itemId) => {
    if (!group?.id) { clearSorting(itemId); return; }
    try {
      const itemIndex = group.items.findIndex(i => i.id === itemId);
      // Item's gone entirely (e.g. deleted by someone else meanwhile) —
      // nothing to write, but still clear sortingIds so nothing's left
      // permanently disabled/collapsed for an id that no longer exists.
      if (itemIndex === -1) { clearSorting(itemId); return; }
      const current = group.items[itemIndex];
      const newOwnQty = (current.ownQuantity || 0) + (current.needQuantity || 0);
      const { borrowStatus, offeredBy, ...rest } = current;

      // Mirrors AddSheet.js/InlineAdd.js's "new items go to the top"
      // convention (Math.min(0, ...) then -1) — applied to packOrder
      // instead of order, over the items Pack currently shows (excluding
      // this one, whose own prior order shouldn't factor into its new
      // placement).
      const packOrders = group.items
        .filter(i => i.id !== itemId && isPackVisible(i))
        .map(i => (i.packOrder !== undefined ? i.packOrder : (i.order ?? 0)));
      const minPackOrder = Math.min(0, ...packOrders);

      const updatedItems = [...group.items];
      updatedItems[itemIndex] = { ...rest, ownQuantity: newOwnQty, needQuantity: 0, packOrder: minPackOrder - 1 };
      await updateDoc(doc(db, 'groups', group.id), { items: updatedItems });
      // Success — the item now has needQuantity: 0, so it naturally drops
      // out of needItems and stops rendering. Also clear sortingIds here
      // (not just on failure) so a later Undo restoring this same item
      // doesn't find it still flagged isSorting from the original sort.
      clearSorting(itemId);
    } catch (err) {
      console.log('Error moving item to Pack:', err);
      onShowToast && onShowToast("Couldn't save that change — check your connection and try again.");
      // The write never landed, so the item never actually left Need —
      // drop it out of sortingIds too, or the row would stay collapsed
      // forever (isSorting stuck true) despite needQuantity being unchanged.
      clearSorting(itemId);
    }
  };

  const handleMoveToPack = (item) => {
    if (pendingSortsRef.current[item.id]) return; // already mid-sort, ignore repeat taps

    // First sort ever dismisses the coach mark for good, whether or not it
    // was actually on screen at this moment.
    if (localStorage.getItem(MOVE_TO_PACK_TOOLTIP_KEY) !== 'true') {
      localStorage.setItem(MOVE_TO_PACK_TOOLTIP_KEY, 'true');
      setMoveToPackHintDismissed(true);
    }

    // Full shallow clone — captures exactly which fields are present (and
    // which aren't) right now, so Undo can restore that exact shape rather
    // than just values (see handleUndo).
    const snapshot = { ...item };

    setSortingIds(prev => new Set(prev).add(item.id));
    onSortedToPack && onSortedToPack();

    const delay = prefersReducedMotion ? 0 : MOVE_TO_PACK_ANIMATION_MS;
    const timer = setTimeout(() => {
      delete pendingSortsRef.current[item.id];
      commitMoveToPack(item.id);
    }, delay);
    pendingSortsRef.current[item.id] = { timer, snapshot };

    clearTimeout(toastTimerRef.current);
    setMoveToPackToast({ itemId: item.id, itemName: item.name, snapshot });
    toastTimerRef.current = setTimeout(() => setMoveToPackToast(null), MOVE_TO_PACK_TOAST_MS);
  };

  const handleUndoMoveToPack = async () => {
    if (!moveToPackToast) return;
    const { itemId, snapshot } = moveToPackToast;
    setMoveToPackToast(null);
    clearTimeout(toastTimerRef.current);

    const pending = pendingSortsRef.current[itemId];
    if (pending) {
      // The write never happened — cancel it and just drop the row back
      // out of its exit animation. group.items was never touched, so
      // there's nothing to restore server-side.
      clearTimeout(pending.timer);
      delete pendingSortsRef.current[itemId];
      clearSorting(itemId);
      return;
    }

    // The write already landed — restore from the snapshot against
    // whatever group.items looks like right now (not a stale closure),
    // replacing only this one item so nothing else gets overwritten. Also
    // clears sortingIds — commitMoveToPack already cleared it on success,
    // but this covers the case where a stale entry somehow lingered, so
    // the restored row never renders permanently disabled/collapsed.
    clearSorting(itemId);
    if (!group?.id) return;
    try {
      const itemIndex = group.items.findIndex(i => i.id === itemId);
      if (itemIndex === -1) return; // item's gone entirely (e.g. deleted meanwhile) — nothing to restore onto
      const updatedItems = [...group.items];
      updatedItems[itemIndex] = snapshot;
      await updateDoc(doc(db, 'groups', group.id), { items: updatedItems });
    } catch (err) {
      console.log('Error undoing move to Pack:', err);
      onShowToast && onShowToast("Couldn't undo that — check your connection and try again.");
    }
  };

  if (showBorrowScreen) return (
    <BorrowScreen group={group} onClose={() => setShowBorrowScreen(false)} />
  );

  if (selectedItem) return (
    <ItemDetail item={selectedItem} group={group} onBack={closeItemDetail} />
  );

  const handleAddItem = (cat) => {
    setActiveCategory(cat || activeCategory);
    setInlineAdding(true);
  };

  // Empty state — two variants sharing the same shell. justFinishedSorting
  // tells them apart (see its own tracking effect above): "just sorted the
  // last item" vs "opened Need with nothing in it to begin with".
  if (needItems.length === 0) {
    return (
      <div className={classes.container}>
        <SearchAndCategories
          categories={group?.categories || []}
          categoryIcons={group?.categoryIcons || {}}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          searchQuery={searchQuery}
          setSearchQuery={handleSearchChange}
          onAddItem={handleAddItem}
          onAddCategory={() => onAddCategory && onAddCategory()}
          onRenameCategory={(oldName, newName) => onRenameCategory && onRenameCategory(oldName, newName)}
          onDeleteCategory={(cat) => onDeleteCategory && onDeleteCategory(cat)}
          onSetCategoryIcon={(cat, key) => onSetCategoryIcon && onSetCategoryIcon(cat, key)}
          onReorderCategories={(cats) => onReorderCategories && onReorderCategories(cats)}
        />
        {inlineAdding && (
          <div className={classes.inlineAddWrap}>
            <InlineAdd activeCategory={activeCategory} group={group} onClose={() => setInlineAdding(false)} screen="need" />
          </div>
        )}
        {justFinishedSorting ? (
          <div className={classes.emptyState}>
            <div className={classes.emptyIcon}>
              <AllSortedIcon color={brandPrimary} />
            </div>
            <p className={classes.emptyText}>Everything you need is covered.</p>
            <button
              className={classes.emptyBtn}
              onClick={() => setCurrentScreen('pack')}
            >
              Go to Pack
            </button>
          </div>
        ) : (
          <div className={classes.emptyState}>
            <div className={classes.emptyIcon}>
              <HandshakeIcon color={brandPrimary} />
            </div>
            <p className={classes.emptyText}>No items needed yet</p>
            <button
              className={classes.emptyBtn}
              onClick={() => setCurrentScreen('list')}
            >
              Mark what you need
            </button>
          </div>
        )}
        {moveToPackToast && (
          <MoveToPackToast
            itemName={moveToPackToast.itemName}
            onUndo={handleUndoMoveToPack}
          />
        )}
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <SearchAndCategories
        group={group}
        categories={group?.categories || []}
        categoryIcons={group?.categoryIcons || {}}
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        searchQuery={searchQuery}
        setSearchQuery={handleSearchChange}
        onAddItem={handleAddItem}
        onAddCategory={() => onAddCategory && onAddCategory()}
        onRenameCategory={(oldName, newName) => onRenameCategory && onRenameCategory(oldName, newName)}
        onDeleteCategory={(cat) => onDeleteCategory && onDeleteCategory(cat)}
        onSetCategoryIcon={(cat, key) => onSetCategoryIcon && onSetCategoryIcon(cat, key)}
        onReorderCategories={(cats) => onReorderCategories && onReorderCategories(cats)}
      />
      {/* Merged summary + "Ask your mates" bar — sits below the category
          tabs, in normal flow (not pinned/sticky — it's discoverable right
          up top already, no need to keep resurfacing it while scrolling).
          Replaces the old separate floating footer button entirely — see
          the old .stickyFooter/.askMatesBtn, now gone. No progress bar
          next to the count: unlike Pack (fixed
          total, packedCount grows toward it), Need's own "total" shrinks as
          items leave the list entirely once sorted, so there's no stable
          denominator to show a percentage against. */}
      <div className={classes.summaryBar}>
        <span className={classes.summaryLabel}>
          {needItems.length} {needItems.length === 1 ? 'thing' : 'things'} still needed
        </span>
        <button
          className={classes.askMatesLink}
          onClick={() => {
            // Actually using it counts as "seen" too, same as the
            // move-to-pack coach mark dismissing itself on first sort —
            // the coach mark's job is done once the user's acted on it,
            // whether or not they tapped "Got it" first.
            if (showAskMatesFooterHint) dismissAskMatesFooterHint();
            setShowBorrowScreen(true);
          }}
        >
          <i className="ti ti-users" aria-hidden="true" />
          Ask your mates
        </button>
      </div>
      {showAskMatesFooterHint && (
        <AskMatesFooterTooltip onDismiss={dismissAskMatesFooterHint} />
      )}
      <div className={classes.list}>
        {inlineAdding && (
          <InlineAdd activeCategory={activeCategory} group={group} onClose={() => setInlineAdding(false)} screen="need" />
        )}
        {activeCategory === '' ? (
          groupedItems.map((grp, gi) => (
            <React.Fragment key={gi}>
              <p className={classes.sectionHeading}>{grp.title}</p>
              {grp.items.map((item) => (
                <React.Fragment key={item.id}>
                  <NeedItem
                    item={item}
                    isHirable={isItemHirable(item)}
                    isSorting={sortingIds.has(item.id)}
                    onMoveToPack={handleMoveToPack}
                    onDetail={openItemDetail}
                    handleDragStart={handleDragStart}
                    handleDragOver={handleDragOver}
                    handleDragEnd={handleDragEnd}
                    festivalName={hireFestivalName}
                    hireUrl={hireFestivalUrl}
                  />
                  {showMoveToPackHint && item.id === firstNeedItemId && (
                    <MoveToPackTooltip onDismiss={dismissMoveToPackHint} />
                  )}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))
        ) : (
          filteredItems.map((item) => (
            <React.Fragment key={item.id}>
              <NeedItem
                item={item}
                isHirable={isItemHirable(item)}
                isSorting={sortingIds.has(item.id)}
                onMoveToPack={handleMoveToPack}
                onDetail={openItemDetail}
                handleDragStart={handleDragStart}
                handleDragOver={handleDragOver}
                handleDragEnd={handleDragEnd}
                festivalName={hireFestivalName}
                hireUrl={hireFestivalUrl}
              />
              {showMoveToPackHint && item.id === firstNeedItemId && (
                <MoveToPackTooltip onDismiss={dismissMoveToPackHint} />
              )}
            </React.Fragment>
          ))
        )}
        {filteredItems.length === 0 && needItems.length > 0 && (
          <p className={classes.emptyText}>
            {searchQuery
              ? `No results for '${searchQuery}'`
              : `No ${activeCategory} items yet. Tap + to add some`}
          </p>
        )}
      </div>

      {moveToPackToast && (
        <MoveToPackToast
          itemName={moveToPackToast.itemName}
          onUndo={handleUndoMoveToPack}
        />
      )}

    </div>
  );
};

export default NeedScreen;
